import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { approvalAPI } from '../../services/api';
import PermissionWrapper from '../../components/Admin/PermissionWrapper';

const AdminApprovals = () => {
    return (
        <PermissionWrapper requiredPermission="universities">
            <AdminApprovalsContent />
        </PermissionWrapper>
    )
}

const AdminApprovalsContent = () => {
    const [filters, setFilters] = useState({
        search: '',
        category: '',
        isActive: '',
        page: 1,
        limit: 10,
        sort: 'name'
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingApproval, setEditingApproval] = useState(null);
    const queryClient = useQueryClient();

    const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm();

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin-approvals', filters],
        queryFn: () => approvalAPI.getAdminApprovals(filters),
        keepPreviousData: true,
    });

    const createMutation = useMutation({
        mutationFn: approvalAPI.createApproval,
        onSuccess: () => {
            toast.success('Approval created successfully!');
            queryClient.invalidateQueries(['admin-approvals']);
            handleCloseModal();
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to create approval');
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => approvalAPI.updateApproval(id, data),
        onSuccess: () => {
            toast.success('Approval updated successfully!');
            queryClient.invalidateQueries(['admin-approvals']);
            handleCloseModal();
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update approval');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: approvalAPI.deleteApproval,
        onSuccess: () => {
            toast.success('Approval deleted successfully!');
            queryClient.invalidateQueries(['admin-approvals']);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete approval');
        }
    });

    const handleFilterChange = (key, value) => {
        console.log('Filter change:', key, value); // Debug log
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: key === 'page' ? value : 1
        }));
    };

    const handleEdit = (approval) => {
        setEditingApproval(approval);
        reset({
            name: approval.name,
            description: approval.description,
            category: approval.category,
            isActive: approval.isActive,
            order: approval.order
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this approval?')) {
            deleteMutation.mutate(id);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingApproval(null);
        reset();
    };

    const onSubmit = (data) => {
        if (editingApproval) {
            updateMutation.mutate({ id: editingApproval._id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    const approvals = data?.data?.approvals || [];
    const pagination = data?.data?.pagination || {};

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Error loading approvals</p>
                    <button onClick={refetch} className="btn-primary">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Manage Approvals | Margdarshak Admin</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Quick Actions */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex justify-end">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="btn-primary"
                            >
                                <PlusIcon className="w-5 h-5 mr-2" />
                                Add Approval
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="relative">
                                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search approvals..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 w-full"
                                />
                            </div>
                            <select
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                            >
                                <option value="">All Categories</option>
                                <option value="government">Government</option>
                                <option value="accreditation">Accreditation</option>
                                <option value="professional">Professional</option>
                                <option value="international">International</option>
                            </select>
                            <select
                                value={filters.isActive}
                                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                            >
                                <option value="">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                            <button
                                onClick={() => setFilters({
                                    search: '',
                                    category: '',
                                    isActive: '',
                                    page: 1,
                                    limit: 10,
                                    sort: 'name'
                                })}
                                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center"
                            >
                                <XMarkIcon className="w-4 h-4 mr-2" />
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Approvals ({pagination.total_count || 0})
                            </h2>
                        </div>

                        {approvals.length === 0 ? (
                            <div className="text-center py-12">
                                <FunnelIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">No approvals found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Name
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Category
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Order
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {approvals.map((approval) => (
                                            <tr key={approval._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {approval.name}
                                                        </div>
                                                        {approval.description && (
                                                            <div className="text-sm text-gray-500">
                                                                {approval.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${approval.category === 'government' ? 'bg-blue-100 text-blue-800' :
                                                        approval.category === 'accreditation' ? 'bg-green-100 text-green-800' :
                                                            approval.category === 'professional' ? 'bg-purple-100 text-purple-800' :
                                                                'bg-orange-100 text-orange-800'
                                                        }`}>
                                                        {approval.category}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${approval.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {approval.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {approval.order}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => handleEdit(approval)}
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(approval._id)}
                                                            className="text-red-600 hover:text-red-900"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="px-6 py-4 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="text-sm text-gray-700">
                                            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of {pagination.total_count} results
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <label className="text-sm text-gray-700">Per page:</label>
                                            <select
                                                value={filters.limit}
                                                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handleFilterChange('page', 1)}
                                            disabled={pagination.current_page === 1}
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            First
                                        </button>
                                        <button
                                            onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                                            disabled={pagination.current_page === 1}
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Previous
                                        </button>

                                        {/* Page Numbers */}
                                        <div className="flex space-x-1">
                                            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                                                let pageNum;
                                                if (pagination.total_pages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (pagination.current_page <= 3) {
                                                    pageNum = i + 1;
                                                } else if (pagination.current_page >= pagination.total_pages - 2) {
                                                    pageNum = pagination.total_pages - 4 + i;
                                                } else {
                                                    pageNum = pagination.current_page - 2 + i;
                                                }

                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => handleFilterChange('page', pageNum)}
                                                        className={`px-3 py-1 border rounded-md text-sm ${pageNum === pagination.current_page
                                                            ? 'bg-primary-600 text-white border-primary-600'
                                                            : 'border-gray-300 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <button
                                            onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                                            disabled={pagination.current_page === pagination.total_pages}
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Next
                                        </button>
                                        <button
                                            onClick={() => handleFilterChange('page', pagination.total_pages)}
                                            disabled={pagination.current_page === pagination.total_pages}
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Last
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                        onClick={handleCloseModal}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {editingApproval ? 'Edit Approval' : 'Add Approval'}
                                </h3>
                            </div>
                            <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Name *
                                    </label>
                                    <input
                                        {...register('name', { required: 'Name is required' })}
                                        className="input"
                                        placeholder="Enter approval name"
                                    />
                                    {errors.name && (
                                        <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        {...register('description')}
                                        className="input"
                                        rows={3}
                                        placeholder="Enter description"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category *
                                    </label>
                                    <select
                                        {...register('category', { required: 'Category is required' })}
                                        className="input"
                                    >
                                        <option value="">Select category</option>
                                        <option value="government">Government</option>
                                        <option value="accreditation">Accreditation</option>
                                        <option value="professional">Professional</option>
                                        <option value="international">International</option>
                                    </select>
                                    {errors.category && (
                                        <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Order
                                    </label>
                                    <input
                                        {...register('order', { valueAsNumber: true })}
                                        type="number"
                                        className="input"
                                        placeholder="Enter order"
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        {...register('isActive')}
                                        type="checkbox"
                                        className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                                    />
                                    <label className="ml-2 block text-sm text-gray-900">
                                        Active
                                    </label>
                                </div>

                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={createMutation.isLoading || updateMutation.isLoading}
                                        className="btn-primary"
                                    >
                                        {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : (editingApproval ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AdminApprovals;
